import React from 'react';

import devLog from '../utils/devLog';

/**
 * ErrorBoundary - V24.99.14 Auto-refresh on ChunkLoadError
 * 
 * FIXES:
 * 1. ChunkLoadError - Automatically refreshes page SILENTLY (no banner)
 * 2. Works on ALL browsers: Safari, Chrome, Firefox, Edge, etc.
 * 3. No user interaction required - just auto-refreshes
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ error, errorInfo });
    
    // V24.99.14: AUTO-REFRESH on ChunkLoadError - NO BANNER
    const isChunkError = error?.name === 'ChunkLoadError' || 
        error?.message?.includes('Loading chunk') ||
        error?.message?.includes('ChunkLoadError') ||
        error?.message?.includes('Failed to fetch dynamically imported module') ||
        error?.message?.includes('Importing a module script failed');
    
    if (isChunkError) {
      devLog.warn('[ErrorBoundary V24.99.14] ChunkLoadError detected - AUTO-REFRESHING');
      
      // V25.2.10: DISABLED AUTO-REFRESH - Causes screen blinking
      // Check refresh limit (prevent infinite loops)
      const refreshKey = 'ep_auto_refresh_count';
      const refreshTimeKey = 'ep_auto_refresh_time';
      const now = Date.now();
      const lastRefreshTime = parseInt(sessionStorage.getItem(refreshTimeKey) || '0');
      let refreshCount = parseInt(sessionStorage.getItem(refreshKey) || '0');
      
      // Reset count if more than 5 minutes since last refresh
      if (now - lastRefreshTime > 300000) {
        refreshCount = 0;
      }
      
      // V25.2.10: DISABLED - Auto-refresh causes screen blinking
      devLog.log('[ErrorBoundary V25.2.10] ❌ Auto-refresh DISABLED - prevents blinking');
      devLog.log('[ErrorBoundary V25.2.10] Error:', error?.message || error);
      
      /* DISABLED V25.2.10 - Causes screen blinking
      // Only auto-refresh up to 3 times
      if (refreshCount < 3) {
        sessionStorage.setItem(refreshKey, (refreshCount + 1).toString());
        sessionStorage.setItem(refreshTimeKey, now.toString());
        
        // Clear ALL caches
        if ('caches' in window) {
          caches.keys().then(names => {
            Promise.all(names.map(name => caches.delete(name))).then(() => {
              devLog.log('[ErrorBoundary V24.99.14] Caches cleared');
            });
          });
        }
        
        // Unregister service workers
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then(registrations => {
            registrations.forEach(r => r.unregister());
          });
        }
        
        // Auto-refresh immediately - NO USER ACTION NEEDED
        setTimeout(() => {
          window.location.reload(true);
        }, 100);
      } else {
        devLog.warn('[ErrorBoundary V24.99.14] Max refresh attempts reached');
      }
      */
    }
  }

  handleRetry = () => {
    // V25.2.10: Only reset state, don't reload
    sessionStorage.removeItem('ep_auto_refresh_count');
    sessionStorage.removeItem('ep_auto_refresh_time');
    
    // Clear caches but don't reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    
    // V25.2.10: Reset error state instead of reloading
    this.setState({ hasError: false, error: null, errorInfo: null });
    devLog.log('[ErrorBoundary V25.2.10] State reset - no reload');
  };

  render() {
    if (this.state.hasError) {
      // Silent mode: render children anyway
      if (this.props.silent) {
        return this.props.children;
      }
      
      // Check if it's a ChunkLoadError - if so, show loading state while refreshing
      const isChunkError = this.state.error?.name === 'ChunkLoadError' ||
                          this.state.error?.message?.includes('Loading chunk') ||
                          this.state.error?.message?.includes('ChunkLoadError') ||
                          this.state.error?.message?.includes('Failed to fetch dynamically imported module');
      
      if (isChunkError) {
        // V24.99.14: Show simple loading while auto-refreshing
        return (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            background: '#f8f9fa'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #e9ecef',
                borderTop: '3px solid #495057',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }} />
              <p style={{ color: '#6c757d', fontSize: '14px' }}>Updating app...</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          </div>
        );
      }
      
      // Non-chunk errors: show error UI
      return (
        <div style={{
          padding: '20px',
          background: '#FEE',
          border: '1px solid #FCC',
          borderRadius: '8px',
          margin: '10px 0'
        }}>
          <h3 style={{ color: '#C00', margin: '0 0 10px 0' }}>Something went wrong</h3>
          <button
            onClick={this.handleRetry}
            style={{
              background: '#C00',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              marginBottom: '10px'
            }}
          >
            Retry
          </button>
          <details style={{ color: '#666', fontSize: '14px' }}>
            <summary style={{ cursor: 'pointer' }}>Error details</summary>
            <pre style={{ 
              background: '#FFF', 
              padding: '10px', 
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '12px'
            }}>
              {this.state.error && this.state.error.toString()}
              {this.state.errorInfo && this.state.errorInfo.componentStack}
            </pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
